import React, { useCallback, useState } from 'react'
import { AlertTriangle, RefreshCw, Search, Target, Loader2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { useProspectCandidates } from '../hooks/useProspectCandidates'
import { useProspectingRun } from '../hooks/useProspectingRun'
import type { CandidateStatus, ProspectingRun, RunStage } from '../types'
import CandidateCard from './CandidateCard'

type Props = {
  workspaceId: string
}

const TABS: { value: CandidateStatus; label: string }[] = [
  { value: 'new', label: 'To review' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
]

const EMPTY_COPY: Record<CandidateStatus, { title: string; body: string }> = {
  new: {
    title: 'Nothing to review',
    body: 'Zeph searches the web for companies matching your primary ideal customer profile (ICP). Click "Find prospects" to start a search; new candidates appear here.',
  },
  accepted: { title: 'No accepted prospects yet', body: 'Companies you accept appear here, along with whether they were added to HubSpot.' },
  rejected: { title: 'No rejected prospects', body: 'Companies you reject appear here. Zeph never suggests them again.' },
}

const STAGE_LABELS: Record<RunStage, string> = {
  queued: 'Starting…',
  queries_generated: 'Planning searches…',
  searching: 'Searching the web…',
  extracted: 'Reading results…',
  verifying: 'Checking company websites…',
  done: 'Finishing…',
}

function finishedMessage(run: ProspectingRun): string {
  if (run.status === 'failed') return 'The search failed. Try again in a few minutes.'
  const n = run.candidates.length
  if (n === 0) return 'The search finished without finding any new companies.'
  return `Found ${n} new compan${n === 1 ? 'y' : 'ies'} to review.`
}

const ProspectReviewSection: React.FC<Props> = ({ workspaceId }) => {
  const { currentTheme: t } = useTheme()
  const [tab, setTab] = useState<CandidateStatus>('new')
  const [notice, setNotice] = useState<{ text: string; tone: 'info' | 'error' } | null>(null)
  const queue = useProspectCandidates(workspaceId, tab)
  const { reload } = queue

  const onRunFinished = useCallback((run: ProspectingRun) => {
    setNotice({ text: finishedMessage(run), tone: run.status === 'failed' ? 'error' : 'info' })
    if (tab === 'new') reload()
  }, [tab, reload])
  const runner = useProspectingRun(workspaceId, onRunFinished)

  const buttonBusy = runner.starting || runner.active

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div role="tablist" aria-label="Candidate status" className="flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: t.hoverBackground }}>
          {TABS.map(({ value, label }) => {
            const selected = tab === value
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setTab(value)}
                className="h-8 px-3 rounded-md text-[13px] font-medium transition-colors"
                style={selected
                  ? { backgroundColor: t.secondary, color: t.text, boxShadow: t.cardShadow }
                  : { color: t.secondrytext }}
              >
                {label}
              </button>
            )
          })}
        </div>
        <button
          type="button"
          onClick={() => { setNotice(null); void runner.start() }}
          disabled={buttonBusy}
          className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: t.accentGradient ?? t.accent, color: t.buttonText }}
        >
          {buttonBusy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
          {runner.active ? 'Searching…' : 'Find prospects'}
        </button>
      </div>

      {runner.active && runner.run && (
        <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-[13px]" style={{ backgroundColor: t.infoBackground, color: t.infoText }} role="status">
          <Loader2 size={14} className="animate-spin shrink-0" />
          {STAGE_LABELS[runner.run.stage]} This usually takes a minute or two — you can keep reviewing.
        </div>
      )}
      {runner.startError && (
        <div className="rounded-lg px-3 py-2 text-[13px]" style={{ backgroundColor: t.errorBackground, color: t.errorText }} role="alert">
          {runner.startError}
        </div>
      )}
      {notice && !runner.active && (
        <div
          className="rounded-lg px-3 py-2 text-[13px]"
          style={notice.tone === 'error'
            ? { backgroundColor: t.errorBackground, color: t.errorText }
            : { backgroundColor: t.successBackground, color: t.successText }}
          role="status"
        >
          {notice.text}
        </div>
      )}

      {queue.loading && (
        <div className="flex flex-col gap-3" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-40 rounded-xl animate-pulse" style={{ backgroundColor: t.hoverBackground }} />
          ))}
        </div>
      )}

      {!queue.loading && queue.error && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl" style={{ backgroundColor: t.errorBackground }}>
            <AlertTriangle size={24} style={{ color: t.errorText }} />
          </div>
          <div>
            <p className="font-semibold text-base" style={{ color: t.text }}>Couldn&apos;t load prospects</p>
            <p className="text-sm mt-1" style={{ color: t.mutedText }}>{queue.error}</p>
          </div>
          <button
            type="button"
            onClick={queue.reload}
            className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium"
            style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}

      {!queue.loading && !queue.error && queue.candidates.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl" style={{ backgroundColor: t.hoverBackground }}>
            <Target size={24} style={{ color: t.mutedText }} />
          </div>
          <div className="max-w-md">
            <p className="font-semibold text-base" style={{ color: t.text }}>{EMPTY_COPY[tab].title}</p>
            <p className="text-sm mt-1" style={{ color: t.mutedText }}>{EMPTY_COPY[tab].body}</p>
          </div>
        </div>
      )}

      {!queue.loading && !queue.error && queue.candidates.length > 0 && (
        <div className="flex flex-col gap-3">
          {queue.candidates.map((candidate) => (
            <CandidateCard
              key={candidate.id}
              candidate={candidate}
              pending={queue.pendingIds.has(candidate.id)}
              error={queue.actionErrors[candidate.id]}
              onAction={(action) => void queue.act(candidate.id, action)}
            />
          ))}
          {queue.nextCursor && (
            <button
              type="button"
              onClick={queue.loadMore}
              disabled={queue.loadingMore}
              className="self-center flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium disabled:opacity-60"
              style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              {queue.loadingMore && <Loader2 size={14} className="animate-spin" />}
              Load more
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default ProspectReviewSection
